import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { TransformControls } from 'three/examples/jsm/controls/TransformControls.js';

/**
 * Builds the viewport: scene, camera, renderer, controls, lights, helpers.
 *
 * The important structural decision here is the split between two top-level
 * groups:
 *
 *   content — everything you create. This is the ONLY thing that gets exported.
 *   helpers — grid and axes. Never exported.
 *
 * That split is what keeps the glTF clean for the Blender -> Unreal pipeline.
 * Without it you end up importing a grid helper and a light rig into UE5 and
 * deleting them by hand every single time.
 */
export function createViewport(container) {
  const scene = new THREE.Scene();
  scene.background = new THREE.Color(0x14161a);

  const camera = new THREE.PerspectiveCamera(
    50,
    container.clientWidth / container.clientHeight,
    0.1,
    1000,
  );
  camera.position.set(5, 5, 5);
  camera.lookAt(0, 0, 0);

  const renderer = new THREE.WebGLRenderer({
    antialias: true,
    // Required for canvas.toDataURL() to return actual pixels. Without it the
    // drawing buffer is cleared after compositing and screenshots come back as
    // a blank image. Costs a little performance; worth it for a personal tool
    // where being able to see the result over the bridge is the whole point.
    preserveDrawingBuffer: true,
  });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
  renderer.setSize(container.clientWidth, container.clientHeight);
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = 1.0;
  renderer.shadowMap.enabled = true;
  renderer.shadowMap.type = THREE.PCFShadowMap;
  container.appendChild(renderer.domElement);

  const controls = new OrbitControls(camera, renderer.domElement);
  controls.enableDamping = true;
  controls.dampingFactor = 0.08;
  controls.target.set(0, 0, 0);
  // LMB is select/gizmo; MMB orbits; Shift+MMB pans (OrbitControls treats
  // shift+ROTATE as pan). RMB is owned by viewport-navigation.js.
  controls.mouseButtons = {
    LEFT: -1,
    MIDDLE: THREE.MOUSE.ROTATE,
    RIGHT: -1,
  };

  // --- Lights -------------------------------------------------------------
  const hemi = new THREE.HemisphereLight(0xbfd4ff, 0x2b2620, 1.1);
  hemi.position.set(0, 20, 0);
  scene.add(hemi);

  const key = new THREE.DirectionalLight(0xffffff, 2.4);
  key.position.set(6, 10, 7);
  key.castShadow = true;
  key.shadow.mapSize.set(2048, 2048);
  key.shadow.camera.near = 0.5;
  key.shadow.camera.far = 60;
  key.shadow.camera.left = -20;
  key.shadow.camera.right = 20;
  key.shadow.camera.top = 20;
  key.shadow.camera.bottom = -20;
  key.shadow.bias = -0.0005;
  scene.add(key);

  // --- Groups -------------------------------------------------------------
  const helpers = new THREE.Group();
  helpers.name = 'Helpers';
  const grid = new THREE.GridHelper(20, 20, 0x4a5568, 0x2a3038);
  grid.material.transparent = true;
  grid.material.opacity = 0.65;
  helpers.add(grid);
  helpers.add(new THREE.AxesHelper(1.5));
  scene.add(helpers);

  const transformControls = new TransformControls(camera, renderer.domElement);
  helpers.add(transformControls.getHelper());

  const content = new THREE.Group();
  content.name = 'Content';
  scene.add(content);

  // --- Resize -------------------------------------------------------------
  function resize() {
    const width = container.clientWidth;
    const height = container.clientHeight;
    if (width === 0 || height === 0) return;
    camera.aspect = width / height;
    camera.updateProjectionMatrix();
    renderer.setSize(width, height);
  }
  window.addEventListener('resize', resize);

  const beforeRender = [];

  function render() {
    for (const hook of beforeRender) hook();
    // Skip while fly-look or a gizmo/box-select drag has set enabled=false.
    // OrbitControls.update() does not honor `enabled` and would otherwise
    // keep applying damping on top of those exclusive gestures.
    if (controls.enabled) controls.update();
    renderer.render(scene, camera);
  }

  function start() {
    renderer.setAnimationLoop(render);
  }

  function pauseRendering() {
    renderer.setAnimationLoop(null);
  }

  function resumeRendering() {
    renderer.setAnimationLoop(render);
  }

  // --- Camera pose --------------------------------------------------------
  // Reported so the bridge can hand the viewpoint back after a reload. Fired on
  // the controls' 'end' event rather than every frame — one message when you
  // let go, not sixty a second while you orbit.

  function getCameraPose() {
    return { position: camera.position.toArray(), target: controls.target.toArray() };
  }

  function setCameraPose(pose) {
    if (!pose?.position || !pose?.target) return;
    camera.position.fromArray(pose.position);
    controls.target.fromArray(pose.target);
    controls.update();
  }

  function onCameraChange(callback) {
    controls.addEventListener('end', () => callback(getCameraPose()));
  }

  // --- WebGL context loss -------------------------------------------------
  // Common on mobile when the tab is backgrounded. Distinct from a reload: the
  // scene graph survives, only the GPU resources go. Three.js re-uploads them
  // on restore, so there is nothing to fix here — but surfacing it means that
  // if the canvas ever goes blank it reads as a known event and not a new
  // mystery.

  function onContextChange(callback) {
    renderer.domElement.addEventListener('webglcontextlost', (event) => {
      // Preventing the default is what lets the context be restored at all.
      event.preventDefault();
      console.warn('[anvil] WebGL context lost');
      callback('lost');
    });
    renderer.domElement.addEventListener('webglcontextrestored', () => {
      console.info('[anvil] WebGL context restored');
      callback('restored');
    });
  }

  return {
    scene,
    camera,
    renderer,
    controls,
    transformControls,
    content,
    helpers,
    render,
    start,
    pauseRendering,
    resumeRendering,
    resize,
    getCameraPose,
    setCameraPose,
    onCameraChange,
    onContextChange,
    onBeforeRender(hook) {
      beforeRender.push(hook);
    },
  };
}
